// SCR-PUBLIC-001 (AP-PLT-07، US-PLT-002؛ C-AGENT-1171): ثبت‌نام عمومی مرکز بدون ورود — نام و نوع مرکز، موبایل مالک با OTP، کد ملی (تطبیق شاهکار)، استان و شهر،
// تخصص و بسته پیشنهادی، پذیرش شرایط استفاده با نسخه (سند نمونه با نشان «نمونه»). ضد ربات: توکن فرم و فیلد تله پنهان (C-AGENT-1157).
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { zodResolver } from '@hookform/resolvers/zod';
import { unwrap, type SignupResult } from '@phoenix/api-client';
import { usePublicApi } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import {
  applyServerErrors,
  ErrorState,
  LoadingState,
  MobileFieldRHF,
  NationalIdFieldRHF,
  normalizeMobile,
  SampleBadge,
  SelectRHF,
  TextFieldRHF,
  toJalali,
  zMobile,
  zNationalId,
  zRequiredText,
} from '@phoenix/ui';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { Link as RouterLink } from 'react-router';
import { z } from 'zod';

export const SCR_PUBLIC_001 = 'SCR-PUBLIC-001';

const CENTER_TYPES = ['Office', 'Clinic', 'Polyclinic', 'Chain'] as const;

const schema = z.object({
  centerName: zRequiredText(200),
  centerType: z.enum(CENTER_TYPES, { error: 'common:validation.required' }),
  ownerMobile: zMobile(),
  code: z.string().regex(/^\d{4,6}$/, { error: 'common:validation.required' }),
  ownerNationalId: zNationalId(),
  provinceCode: z.string().min(1, { error: 'common:validation.required' }),
  cityCode: z.string().min(1, { error: 'common:validation.required' }),
  specialty: z.string().min(1, { error: 'common:validation.required' }),
  packCode: z.string(),
  acceptTerms: z.literal(true, { error: 'common:validation.required' }),
  website: z.string(),
});

type SignupForm = z.input<typeof schema>;

export function SignupPage() {
  const { t } = useTranslation('plt');
  const api = usePublicApi();
  const [otpId, setOtpId] = useState<string>();
  const [result, setResult] = useState<SignupResult>();
  const start = useQuery({
    queryKey: ['platform', 'public', 'signup'],
    queryFn: () => unwrap(api.POST('/api/v1/platform/public/signup:start')),
    staleTime: Infinity,
    retry: false,
  });
  const { control, handleSubmit, setError, getValues, trigger } = useForm<SignupForm>({
    resolver: zodResolver(schema),
    defaultValues: {
      centerName: '',
      centerType: 'Clinic',
      ownerMobile: '',
      code: '',
      ownerNationalId: '',
      provinceCode: '',
      cityCode: '',
      specialty: '',
      packCode: '',
      acceptTerms: false as unknown as true,
      website: '',
    },
  });
  const province = useWatch({ control, name: 'provinceCode' });
  const cities = useMemo(
    () => start.data?.provinces.find((item) => item.code === province)?.cities ?? [],
    [start.data, province],
  );
  useEffect(() => {
    document.title = `${t('plt:signup.title')} · ${t('shell:app.staff_name')}`;
  }, [t]);

  const sendOtp = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/api/v1/platform/public/signup:send-otp', {
          body: { formToken: start.data?.formToken ?? null, mobile: normalizeMobile(getValues('ownerMobile')) ?? null },
        }),
      ),
    onSuccess: (challenge) => setOtpId(challenge.otpId),
  });
  const submit = useMutation({
    mutationFn: (form: z.output<typeof schema>) =>
      unwrap(
        api.POST('/api/v1/platform/public/signup', {
          body: {
            formToken: start.data?.formToken ?? null,
            otpId: otpId ?? null,
            code: form.code,
            centerName: form.centerName,
            centerType: form.centerType,
            ownerMobile: form.ownerMobile,
            ownerNationalId: form.ownerNationalId,
            provinceCode: form.provinceCode,
            cityCode: form.cityCode,
            specialty: form.specialty,
            packCode: form.packCode || null,
            acceptTerms: form.acceptTerms,
            termsVersion: start.data?.termsVersion ?? null,
            website: form.website || null,
          },
        }),
      ),
    onSuccess: setResult,
    onError: (error) =>
      applyServerErrors(error, setError, {
        fields: ['centerName', 'centerType', 'ownerMobile', 'ownerNationalId', 'provinceCode', 'cityCode', 'specialty', 'code'],
        codeFields: { 'PHX-PLT-0102': 'ownerNationalId', 'PHX-PLT-0107': 'code', 'PHX-PLT-0303': 'cityCode' },
      }),
  });

  const data = start.data;
  return (
    <Box data-screen-id={SCR_PUBLIC_001} sx={{ minHeight: '100vh', bgcolor: 'background.default', p: { xs: 1.5, sm: 3 } }}>
      <Paper variant="outlined" component="main" sx={{ p: { xs: 2, sm: 4 }, maxWidth: 640, mx: 'auto' }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2, alignItems: 'center' }}>
          <LocalHospitalOutlinedIcon color="primary" />
          <Typography sx={{ fontWeight: 600 }}>{t('shell:app.staff_name')}</Typography>
        </Stack>
        <Typography variant="h1" sx={{ mb: 1 }}>
          {t('plt:signup.title')}
        </Typography>
        {data ? (
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            {t('plt:signup.subtitle', { days: data.trialDays })}
          </Typography>
        ) : null}
        {start.isPending ? <LoadingState rows={4} /> : null}
        {start.error ? <ErrorState error={start.error} onRetry={() => void start.refetch()} /> : null}
        {result ? (
          <Stack spacing={2} data-testid="signup-success">
            <Alert severity="success">
              <Typography sx={{ fontWeight: 600 }}>{t('plt:signup.success_title')}</Typography>
              {t('plt:signup.success_text')}
            </Alert>
            <Typography data-testid="signup-trial-until">{t('plt:signup.trial_until', { date: toJalali(result.trialEndsOn) })}</Typography>
            {result.identityStatus === 'Pending' ? <Alert severity="warning">{t('plt:signup.identity_pending')}</Alert> : null}
            <Button component={RouterLink} to="/" variant="contained" sx={{ alignSelf: 'flex-start' }}>
              {t('plt:signup.login')}
            </Button>
          </Stack>
        ) : data ? (
          <Stack component="form" spacing={2} noValidate onSubmit={handleSubmit((form) => submit.mutate(form as unknown as z.output<typeof schema>))}>
            <TextFieldRHF name="centerName" control={control} labelKey="plt:signup.center_name" required />
            <SelectRHF
              name="centerType"
              control={control}
              labelKey="plt:signup.center_type"
              options={CENTER_TYPES.map((value) => ({ value, labelKey: `plt:center_type.${value}` }))}
              required
            />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'flex-start' } }}>
              <MobileFieldRHF name="ownerMobile" control={control} labelKey="plt:signup.mobile" required />
              <Button
                variant="outlined"
                onClick={async () => {
                  if (await trigger('ownerMobile')) {
                    sendOtp.mutate();
                  }
                }}
                disabled={sendOtp.isPending}
                sx={{ mt: { sm: 1 } }}
                data-testid="signup-send-otp"
              >
                {t(otpId ? 'plt:signup.resend_otp' : 'plt:signup.send_otp')}
              </Button>
            </Stack>
            {otpId ? <Alert severity="info">{t('plt:signup.otp_sent', { mobile: getValues('ownerMobile') })}</Alert> : null}
            {sendOtp.error ? <ErrorState error={sendOtp.error} compact /> : null}
            {otpId ? (
              <TextFieldRHF
                name="code"
                control={control}
                labelKey="plt:signup.code"
                required
                slotProps={{ htmlInput: { inputMode: 'numeric', dir: 'ltr', autoComplete: 'one-time-code', 'data-testid': 'signup-code' } }}
              />
            ) : null}
            <NationalIdFieldRHF name="ownerNationalId" control={control} labelKey="plt:signup.national_id" required />
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <SelectRHF
                name="provinceCode"
                control={control}
                labelKey="plt:signup.province"
                options={data.provinces.map((item) => ({ value: item.code, label: item.name }))}
                required
                fullWidth
              />
              <SelectRHF
                name="cityCode"
                control={control}
                labelKey="plt:signup.city"
                options={cities.map((item) => ({ value: item.code, label: item.name }))}
                required
                fullWidth
              />
            </Stack>
            <SelectRHF
              name="specialty"
              control={control}
              labelKey="plt:signup.specialty"
              options={data.specialties.map((item) => ({ value: item.code, label: item.name }))}
              required
            />
            {data.specialties.some((item) => item.isSample) ? (
              <Box>
                <SampleBadge />
              </Box>
            ) : null}
            <SelectRHF
              name="packCode"
              control={control}
              labelKey="plt:signup.pack"
              options={[{ value: '', labelKey: 'plt:signup.pack_none' }, ...data.packs.map((item) => ({ value: item.code, label: item.name }))]}
            />
            {/* فیلد تله ربات: برای کاربر پنهان و بیرون از ترتیب Tab (C-AGENT-1157). */}
            <Box aria-hidden sx={{ position: 'absolute', insetInlineStart: -10000, width: 1, height: 1, overflow: 'hidden' }}>
              <Controller
                name="website"
                control={control}
                render={({ field }) => <TextField {...field} label={t('plt:signup.website')} tabIndex={-1} slotProps={{ htmlInput: { tabIndex: -1, autoComplete: 'off' } }} />}
              />
            </Box>
            <Controller
              name="acceptTerms"
              control={control}
              render={({ field, fieldState }) => (
                <Stack spacing={0.5}>
                  <FormControlLabel
                    control={<Checkbox checked={Boolean(field.value)} onChange={(_, checked) => field.onChange(checked)} inputProps={{ 'data-testid': 'signup-terms' } as never} />}
                    label={t('plt:signup.terms', { version: data.termsVersion })}
                  />
                  {data.termsIsSample ? (
                    <Box>
                      <SampleBadge />
                    </Box>
                  ) : null}
                  {fieldState.error ? (
                    <Typography variant="caption" color="error">
                      {t(fieldState.error.message ?? 'common:validation.required')}
                    </Typography>
                  ) : null}
                </Stack>
              )}
            />
            {submit.error ? <ErrorState error={submit.error} compact /> : null}
            <Button type="submit" variant="contained" size="large" disabled={!otpId || submit.isPending} data-testid="signup-submit">
              {t('plt:signup.submit')}
            </Button>
          </Stack>
        ) : null}
      </Paper>
    </Box>
  );
}
